import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import {
  brandUniverseGraphKey,
  cancelGraphFetches,
  removeFactsFromGraph,
  restoreFactsToGraph,
} from "./universeCache";

const deleteFactKey = (brandId: string | null) =>
  ["deleteBrandUniverseFact", brandId] as const;

export function useDeleteBrandUniverseFact(brandId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: deleteFactKey(brandId),
    mutationFn: async (factId: string) => {
      if (!brandId) throw new Error("No brand selected");
      await axiosInstance.delete(`/brand/${brandId}/universe/facts/${factId}`);
    },
    // Optimistic: the planet leaves on confirm, and only this fact comes back
    // if the server refuses.
    onMutate: async (factId) => {
      if (!brandId) return undefined;
      await cancelGraphFetches(queryClient, brandId);
      return { removed: removeFactsFromGraph(queryClient, brandId, [factId]) };
    },
    onError: (error, _factId, context) => {
      if (brandId && context) restoreFactsToGraph(queryClient, brandId, context.removed);
      queue.add({
        title: "Couldn't delete the fact",
        description: getAiRequestErrorMessage(error, "Please try again."),
        variant: "error",
      });
    },
    // Reconcile once the last delete settles: a refetch in between would bring
    // back facts whose requests are still in flight. The settling mutation
    // still counts itself here, hence > 1.
    onSettled: () => {
      if (!brandId) return;
      if (queryClient.isMutating({ mutationKey: deleteFactKey(brandId) }) > 1) return;
      void queryClient.invalidateQueries({ queryKey: brandUniverseGraphKey(brandId) });
    },
  });
}
